/**
 * `mount(canvas, { tier })`: the dungeon view. It owns the three.js renderer,
 * the scene, the shared geometries and materials, the current floor's world,
 * the unit proxies, the ground ring effects, the animation clock and the
 * Medium tier's parts. One scene graph for both tiers (ADR 0006): the tier
 * selects the materials, the shadow map, the lens pass and the pixel ratio;
 * the floor build is shared, and `setTier` switches without rebuilding it.
 *
 * - **Off** (slice 1): unlit `MeshBasicMaterial` with baked vertex colours,
 *   pixel ratio 1, no lights, no shadow map, no post-processing, blob shadows;
 *   since slice 2b the smooth fog of war sampled from the exploration mask.
 * - **Medium** (slice 2a): the pixel-art surfaces, the merged rock mass, the
 *   baked light volume with its fixtures, one directional light with a 1024
 *   shadow map and the hemisphere fill, the units' cut-out shadows, and one
 *   merged lens pass, at a pixel ratio of at most 1.5.
 *
 * Rendering is on demand (ADR 0005): a frame on `show`, `resize`, `setTier`,
 * the end of `play` and during an animation. At Off nothing renders while
 * idle. At Medium the lens animates its grain and dust motes and the flames
 * flicker, so while idle it renders at most `presentation.medium.idleFps`
 * (30) frames a second, and none while the page is hidden.
 *
 * Sprites (slice 3): `loadSprites` fetches and repacks the given sheets
 * (`sprites.ts`); a unit of a loaded species draws as a sprite plane, any
 * other as its proxy, and a sheet that lands after `show` switches its units
 * on the next frame (the sheets that land before the same frame install
 * together, then one frame). While a sprite's loop plays on screen, Off
 * renders idle frames too, at most `presentation.sprite.idleFps` a second
 * (none while the page is hidden). At Medium the static shadow map is redrawn
 * when a sprite moves, turns, changes anim, appears or goes, when a sheet
 * lands, and on the first frame after `play` ends; never for a loop's frame
 * steps alone. The sprite programs are compiled with the floor
 * (`precompile`'s staging) once sheets are asked for, so a landing compiles
 * nothing in an ordinary frame.
 *
 * Text never goes into the canvas (ADR 0005): `project` and `projectTile`
 * give the screen position a DOM label needs, and `play`'s `onEvent` tells
 * the caller when each event starts, so labels keep step with gestures.
 *
 * A lost WebGL context (ADR 0006, the Rendering page's "iOS Safari") stops
 * rendering and frees every GPU resource while the context is gone (GL calls
 * are no-ops then, so nothing stale is deleted later); on
 * `webglcontextrestored` the view rebuilds them from the last `RunState` it
 * was given: the shared geometries and materials, the Medium tier's
 * materials, lens targets and atlas, and the floor with its explored tiles.
 * The unit poses and the animation clock carry on; the public surface does
 * not change.
 */
import type { ItemIcon, SpriteSource, Theme } from '@ab/content-schema';
import type { Event, Floor, Pos, RunState } from '@ab/game-core';
import * as THREE from 'three';
import { shadeGeometry } from './batch.ts';
import { follow, leadFor } from './camera.ts';
import { presentation } from './constants.ts';
import { createBandGeometry, createBarrelGeometry, createPebbleGeometry, createTuftGeometry } from './dressing.ts';
import { type Effects, createEffects } from './effects.ts';
import { type Stage, playEvent } from './events.ts';
import { type GpuTimer, createGpuTimer } from './gpu-timer.ts';
import { halfFloatTarget } from './graphics.ts';
import { tileIndex } from './grid.ts';
import { addShaderHook } from './material-hooks.ts';
import { type Medium, createMedium } from './medium.ts';
import { Animator } from './motion.ts';
import { type Palette, paletteOf } from './palette.ts';
import { precompile, precompileStaged } from './precompile.ts';
import { offFogFragment, offFogFragmentHeader, offFogVertex, offFogVertexHeader } from './shaders/fog.ts';
import { type SpriteSheet, loadSheet, preparedSheet } from './sprites.ts';
import { type SpriteState, UnitLayer, type UnitPose } from './units.ts';
import { type Shared, type World, buildWorld } from './world.ts';

export const qualityTiers = ['off', 'medium'] as const;
export type QualityTier = (typeof qualityTiers)[number];

export type ViewStats = {
  readonly tier: QualityTier;
  /** The renderer's pixel ratio and the canvas's backing size in pixels. */
  readonly pixelRatio: number;
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  /** Draw calls and triangles of the last frame (`renderer.info`, every pass). */
  readonly drawCalls: number;
  readonly triangles: number;
  /**
   * The last frame: whether it redrew the Medium shadow map (its draws then
   * include the shadow pass; tests compare only frames that did not).
   */
  readonly lastFrame: { readonly shadowRedraw: boolean };
  /**
   * What each of the last 120 frames cost the CPU to submit, ms, oldest first:
   * from the start to the end of `render` (all passes), whatever paced it.
   */
  readonly cpuFrameSamples: readonly number[];
  /**
   * Intervals between the last 120 back-to-back animation frames, ms, oldest
   * first: the frame rate while something moves. Idle gaps, hidden pages and
   * tier changes are never counted.
   */
  readonly animationFrameSamples: readonly number[];
  /** GPU time of the last measured frame, ms (`EXT_disjoint_timer_query_webgl2`); null where the extension is missing. */
  readonly gpuMs: number | null;
  /** The last 1024 GPU frame times, ms, oldest first. */
  readonly gpuSamples: readonly number[];
  /** How many GPU frame times have been measured since mount. */
  readonly gpuMeasured: number;
  readonly frames: number;
  /** Time from the start of the last floor build to its first frame on screen (a pixel read back waits for the GPU), ms. */
  readonly buildMs: number | null;
  /** Draw calls and triangles of the last floor's first frame (at Medium it also draws the shadow map). */
  readonly buildFrame: { readonly drawCalls: number; readonly triangles: number } | null;
  /** Geometries and textures the renderer holds on the GPU (`renderer.info.memory`). */
  readonly geometries: number;
  readonly textures: number;
  /** Shader programs the renderer holds (`renderer.info.programs`). */
  readonly programs: number;
  /**
   * The follow camera of the last frame: its target (world coordinates), the
   * leader's displayed position (game coordinates) and the portrait
   * look-ahead in tiles (the target is the leader's position led north by it).
   */
  readonly camera: { readonly target: readonly [number, number, number]; readonly leader: readonly [number, number] | null; readonly lead: number };
  /** How many times the WebGL context was lost and rebuilt since mount. */
  readonly contextLosses: number;
  readonly contextRestores: number;
  /** The most draw calls and triangles of any frame (animation frames included) since the last floor build or `resetPeak`. */
  readonly peak: { readonly drawCalls: number; readonly triangles: number };
  /** Item billboards drawn on the floor (not picked up). */
  readonly itemsShown: number;
  /**
   * Sprite sheets installed (landed, not merely asked for), units drawn as
   * sprites and as proxies now (the ones the player can see), and the texels
   * of the atlases on the GPU (the sheets in use on the floor).
   */
  readonly sprites: { readonly loaded: number; readonly unitsDrawn: number; readonly proxies: number; readonly atlasTexels: number };
  /** The scene view's own numbers (slice 6a); null in the dungeon. */
  readonly scene: SceneStats | null;
};

/**
 * A scene view's numbers (slice 6a, `mountScene`): the scene shown, its GPU
 * points drawn per kind, its point, spot and shadow-casting lights now, its
 * birds drawn, and the merged static meshes its build made.
 */
export type SceneStats = {
  readonly id: string | null;
  /** GPU points drawn per kind; `motes` the airship's drifting motes (ADR 0033). */
  readonly particles: { readonly dust: number; readonly smoke: number; readonly leaves: number; readonly motes: number };
  readonly lights: { readonly point: number; readonly spot: number; readonly shadowed: number };
  /** Birds drawn (the flocks' buffer, Medium only). */
  readonly birds: number;
  /** The walking model's revision the last frame drew (-1 before one). */
  readonly revision: number;
  readonly batchedMeshes: number;
  /** The camera's vertical field of view now (degrees) and its framing. */
  readonly fov: number;
  readonly framing: string | null;
  /** A ship scene's (ADR 0033): the preset shown, the flight's blend, each room's roof reveal (0 shown .. 1 dissolved), the hatch display's phase, the crew on deck and the clutter cards drawn, the lens's sun glare now; null in a town. */
  readonly ship: ShipStats | null;
};

export type ShipStats = {
  readonly preset: string;
  readonly flight: number;
  readonly reveals: Readonly<Record<string, number>>;
  readonly hatch: string;
  /** Crew members on deck now (their posts by the time of day and the flight), drawn as the residents are. */
  readonly crew: number;
  readonly clutter: number;
  readonly sky: boolean;
  /** The lens's sun glare uniform at the last Medium frame (the dawn's and dusk's veil while the sun is on screen). */
  readonly glare: number;
};

export type ShowOptions = {
  readonly seed: number;
  readonly theme: Theme;
  /** Each item's floor pattern by item id (the content records' `icon`). */
  readonly itemIcons: Readonly<Record<string, ItemIcon>>;
  /**
   * Draw the leader here instead of at its tile (captures): the floor, its
   * fixtures and light are built from `state` as it is; only the leader's
   * displayed pose, the camera and the fog of war start from `leaderAt`.
   */
  readonly leaderAt?: Pos;
};

export type PlayOptions = {
  /** The new floor's `enter_floor` seed, when the events include a `floor_entered`. */
  readonly seed?: number;
  /** Called as the view starts animating each event (before its gesture), with its index in `events`. */
  readonly onEvent?: (event: Event, index: number) => void;
};

/** A screen position in CSS pixels of the canvas; `visible` is false for a unit the player cannot see or one off screen. */
export type Projection = { readonly x: number; readonly y: number; readonly visible: boolean };

export type DungeonView = {
  /** Builds `state.floor` (which must be set) with the standing units, and down units lying at their tile. */
  show(state: RunState, options: ShowOptions): void;
  /**
   * Animates `events` in order, one at a time; resolves when the last ends.
   * A `floor_entered` rebuilds the floor from `after` with `options.seed` (the
   * new floor's `enter_floor` seed) and the theme of the last `show`.
   */
  play(events: readonly Event[], before: RunState, after: RunState, options?: PlayOptions): Promise<void>;
  /** Switches the tier at runtime: materials, lights, shadow map, lens and pixel ratio; the floor is not rebuilt. */
  setTier(tier: QualityTier): void;
  readonly tier: QualityTier;
  /** 1 is normal speed; 0 makes every animation instant. */
  setSpeed(speed: number): void;
  readonly busy: boolean;
  resize(width: number, height: number): void;
  /** Where a unit's head is on screen now (a DOM label's anchor); null for a unit not on the floor. */
  project(unitId: string): Projection | null;
  /** Where a tile's centre on the ground is on screen now. */
  projectTile(pos: Pos): Projection;
  /**
   * The tile under a point of the canvas (CSS pixels, as `projectTile` gives
   * them): the camera's ray through the point meets the ground plane there
   * (the inverse of `projectTile`). Null when the ray misses the floor's
   * grid or the tile is not explored (the fog of war), so a caller can never
   * aim through fog. Presentation only: whether the tile can be walked to is
   * game-core's call.
   */
  pickTile(x: number, y: number): Pos | null;
  /** The explored tiles of the current floor (the fog of war's seen set), per tile index `y × width + x`. */
  explored(): readonly boolean[];
  stats(): ViewStats;
  /** Starts the `peak` of `stats` again from the next frame. */
  resetPeak(): void;
  /** How many events of each type `play` has handled since mount. */
  eventCounts(): Readonly<Partial<Record<Event['type'], number>>>;
  /**
   * Fetches and repacks sprite sheets (`fetch` + `createImageBitmap`); once per
   * id, and a sheet this page already prepared installs at once. The sheets
   * that land before the same frame install together, then one frame draws
   * them. A sheet that fails to load or has no usable `idle` is not installed
   * (one console warning names it and why; its units keep their proxies).
   * Resolves, once every sheet asked for has landed or failed, with the ids
   * installed in this view.
   */
  loadSprites(sources: readonly SpriteSource[]): Promise<readonly string[]>;
  /** A sprite unit's anim, drawn facing, flip and frame index now; null for a proxy or a unit not on the floor. */
  spriteState(unitId: string): SpriteState | null;
  /** Frees everything; returns the geometries and textures still on the GPU just before the renderer goes (0 and 0 when nothing leaked). */
  dispose(): { readonly geometries: number; readonly textures: number };
};

const syncPixel = new Uint8Array(4);

/** The view-lifetime geometries and materials, rebuilt after a lost context. */
type Gpu = {
  readonly shared: Shared;
  readonly disc: THREE.BufferGeometry;
  readonly shadowMaterial: THREE.Material;
  readonly medium: Medium;
  readonly effects: Effects;
  readonly gpu: GpuTimer;
  /** The Off fog colour (the theme's). */
  setFog(color: THREE.Color): void;
  dispose(): void;
};

const createGpu = (renderer: THREE.WebGLRenderer, scene: THREE.Scene, carry?: GpuTimer): Gpu => {
  const geometries = {
    box: shadeGeometry(new THREE.BoxGeometry(1, 1, 1)),
    round: shadeGeometry(new THREE.CylinderGeometry(1, 1, 1, presentation.roundSegments)),
    pebble: shadeGeometry(createPebbleGeometry()),
    barrel: shadeGeometry(createBarrelGeometry()),
    band: shadeGeometry(createBandGeometry()),
    tuft: createTuftGeometry(),
  };
  const disc = new THREE.CircleGeometry(1, presentation.unit.shadow.segments).rotateX(-Math.PI / 2);
  const solid = new THREE.MeshBasicMaterial({ vertexColors: true });
  const offFog = new THREE.MeshBasicMaterial({ vertexColors: true });
  const fogUniforms = {
    abMask: { value: null as THREE.Texture | null },
    abFloorSize: { value: new THREE.Vector2(1, 1) },
    abFog: { value: new THREE.Color() },
    abFade: { value: presentation.fog.fade as number },
    abDarken: { value: presentation.fog.darken as number },
  };
  addShaderHook(offFog, {
    key: 'off-fog',
    uniforms: fogUniforms,
    vertexHeader: offFogVertexHeader,
    fragmentHeader: offFogFragmentHeader,
    vertex: [{ chunk: 'project_vertex', after: offFogVertex }],
    fragment: [{ chunk: 'color_fragment', after: offFogFragment }],
  });
  // Blob shadows darken the ground without writing depth, so overlapping discs never fight.
  const shadowMaterial = new THREE.MeshBasicMaterial({ transparent: true, opacity: presentation.unit.shadow.opacity, depthWrite: false });
  const medium = createMedium(scene, halfFloatTarget(renderer.getContext()));
  const shared: Shared = {
    ...geometries,
    solid,
    offFog,
    bindOffFog: (mask: THREE.Texture, floor: Floor) => {
      fogUniforms.abMask.value = mask;
      fogUniforms.abFloorSize.value.set(floor.width, floor.height);
    },
    medium: medium.materials,
    billboardLight: medium.billboardLight,
  };
  const effects = createEffects(scene);
  const gpu = createGpuTimer(renderer.getContext(), carry);
  return {
    shared,
    disc,
    shadowMaterial,
    medium,
    effects,
    gpu,
    setFog(color) {
      fogUniforms.abFog.value.copy(color);
    },
    dispose() {
      effects.dispose();
      medium.dispose();
      for (const g of [...Object.values(geometries), disc]) g.dispose();
      for (const m of [solid, offFog, shadowMaterial]) m.dispose();
      gpu.dispose();
    },
  };
};

export const mount = (canvas: HTMLCanvasElement, options: { readonly tier: QualityTier }): DungeonView => {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance' });
  renderer.info.autoReset = false;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.shadowMap.autoUpdate = false;
  renderer.toneMappingExposure = presentation.medium.exposure;
  const scene = new THREE.Scene();
  const c = presentation.camera;
  const camera = new THREE.PerspectiveCamera(c.fov, 1, c.near, c.far);
  let gpu = createGpu(renderer, scene);
  const units = new UnitLayer(scene, gpu.shared.box, gpu.disc, gpu.shared.solid, gpu.shadowMaterial, gpu.shared.billboardLight);
  /** Sheet ids asked for (in flight or installed), and installed (landed), in this view. */
  const requestedSprites = new Set<string>();
  const installedSprites = new Set<string>();
  const counts: Partial<Record<Event['type'], number>> = {};
  let tier: QualityTier = options.tier;
  let world: World | null = null;
  let theme: Theme | null = null;
  let palette: Palette | null = null;
  let icons: Readonly<Record<string, ItemIcon>> = {};
  const mediumFog = new THREE.Color();
  let seed = 0;
  /** The state the current floor was built from (its fixtures, dressing and light follow where things stood then). */
  let built: RunState | null = null;
  /** Markers hidden on this floor since it was built (items picked up, springs drunk): a rebuild hides them again. */
  const hiddenMarkers = new Set<string>();
  /** The explored tiles when the context was lost, and the floor they belong to. */
  let seenSnapshot: { readonly floor: Floor; readonly tiles: readonly number[] } | null = null;
  let peak = { drawCalls: 0, triangles: 0 };
  let leaderId: string | null = null;
  let last = { drawCalls: 0, triangles: 0, frames: 0 };
  let shadowRedraw = false;
  let size = { width: 1, height: 1 };
  let buildMs: number | null = null;
  let buildFrame: ViewStats['buildFrame'] = null;
  const followed = { target: [0, 0, 0] as [number, number, number], leader: null as [number, number] | null };
  const cpuSamples: number[] = [];
  let disposed = false;
  let lost = false;
  let losses = 0;
  let restores = 0;

  const leaderPose = (): UnitPose | undefined => (leaderId === null ? undefined : units.get(leaderId));
  const tileOf = (w: World, p: UnitPose): number => tileIndex(w.floor, Math.round(p.x), Math.round(p.y));
  /** Party units are always drawn; an enemy only on an explored tile (fog of war). */
  const visible = (p: UnitPose): boolean => p.side === 'party' || (world !== null && world.isSeen(tileOf(world, p)));
  const elevation = (p: UnitPose): number => (world !== null && world.dais.has(tileOf(world, p)) ? presentation.marker.dais.elevation : 0);
  const groundAt = (x: number, y: number): number => (world !== null && world.dais.has(tileIndex(world.floor, Math.round(x), Math.round(y))) ? presentation.marker.dais.elevation : 0);

  const render = (): void => {
    if (disposed || lost || world === null || palette === null) return;
    const w = world;
    const lead = leaderPose();
    const now = performance.now();
    if (lead) {
      const target = follow(camera, lead.x, lead.y);
      followed.target = [target.x, target.y, target.z];
      followed.leader = [lead.x, lead.y];
    }
    // After the camera moved: the sprite planes take its rotation. A changed sprite at Medium redraws the shadow map.
    if (units.update(palette, visible, elevation, camera, now) && tier === 'medium') renderer.shadowMap.needsUpdate = true;
    gpu.effects.update(groundAt);
    w.billboards.update(camera, (tile) => w.isSeen(tile), tier === 'medium' ? mediumFog : palette.fog);
    // Read before the frame: three clears the flag once the shadow map is drawn.
    shadowRedraw = renderer.shadowMap.enabled && renderer.shadowMap.needsUpdate;
    renderer.info.reset();
    gpu.gpu.begin();
    if (tier === 'medium') gpu.medium.render(renderer, scene, camera, w, units.proxies(), lead, visible, elevation, now / 1000);
    else renderer.render(scene, camera);
    gpu.gpu.end();
    cpuSamples.push(performance.now() - now);
    if (cpuSamples.length > presentation.stats.frames) cpuSamples.shift();
    last = { drawCalls: renderer.info.render.calls, triangles: renderer.info.render.triangles, frames: last.frames + 1 };
    peak = { drawCalls: Math.max(peak.drawCalls, last.drawCalls), triangles: Math.max(peak.triangles, last.triangles) };
  };

  const clock = new Animator(render, presentation.stats.frames);

  /** The sprite programs to compile with a floor: only once sheets are asked for (none on a machine without them). */
  const spriteStaging = (): ReturnType<UnitLayer['spriteStaging']> | null => (requestedSprites.size > 0 ? units.spriteStaging() : null);

  // Idle frames, capped, and none while the page is hidden: at Medium always (grain, motes, flames), at Off
  // only while a unit holds a charge (its tremble) or a sprite's loop plays on screen.
  let idleFrame: number | null = null;
  let lastIdle = 0;
  const doc = canvas.ownerDocument;
  const onScreenAt = new THREE.Vector3();
  const onScreen = (p: UnitPose): boolean => {
    const v = onScreenAt.set(p.x, elevation(p), -p.y).project(camera);
    return v.z < 1 && Math.abs(v.x) <= 1 && Math.abs(v.y) <= 1;
  };
  const wantsIdle = (): boolean => tier === 'medium' || units.all().some((p) => p.charging && !p.gone) || units.animating((p) => visible(p) && onScreen(p));
  const idle = (time: number): void => {
    idleFrame = null;
    if (disposed || lost || !wantsIdle() || doc.hidden) return;
    const fps = tier === 'medium' ? presentation.medium.idleFps : presentation.sprite.idleFps;
    if (!clock.busy && time - lastIdle >= 1000 / fps) {
      lastIdle = time;
      render();
    }
    idleFrame = requestAnimationFrame(idle);
  };
  const startIdle = (): void => {
    if (idleFrame === null && wantsIdle() && !doc.hidden && !disposed && !lost) idleFrame = requestAnimationFrame(idle);
  };
  const stopIdle = (): void => {
    if (idleFrame !== null) cancelAnimationFrame(idleFrame);
    idleFrame = null;
  };
  const onVisibility = (): void => {
    clock.breakStreak();
    if (doc.hidden) stopIdle();
    else startIdle();
  };
  doc.addEventListener('visibilitychange', onVisibility);

  const applySize = (): void => {
    renderer.setSize(size.width, size.height, false);
    camera.aspect = size.width / Math.max(1, size.height);
    camera.updateProjectionMatrix();
    if (tier === 'medium') {
      const buffer = renderer.getDrawingBufferSize(new THREE.Vector2());
      gpu.medium.lens.setSize(buffer.x, buffer.y);
    }
  };

  /** Everything the tier decides: pixel ratio, tone mapping, shadow map, lights, materials and what is drawn. */
  const applyTier = (): void => {
    const m = tier === 'medium';
    const medium = gpu.medium;
    renderer.setPixelRatio(m ? Math.min(window.devicePixelRatio, presentation.medium.pixelRatio) : 1);
    renderer.toneMapping = m ? THREE.ACESFilmicToneMapping : THREE.NoToneMapping;
    renderer.shadowMap.enabled = m;
    renderer.shadowMap.needsUpdate = m;
    medium.setActive(m);
    units.setTier(m ? medium.materials.lit : gpu.shared.solid, !m);
    if (m) renderer.shadowMap.needsUpdate = true;
    world?.setTier(tier);
    if (m && world !== null && theme !== null) medium.bindFloor(world, theme, seed);
    applySize();
    if (!m) medium.release();
    if (m && world !== null) precompile(renderer, scene, camera, medium.lens.target, medium.lens.materials, spriteStaging());
    const staged = spriteStaging();
    if (!m && world !== null && staged !== null) precompileStaged(renderer, scene, camera, staged);
  };

  /**
   * The world for `state`, rendered once before the old one is disposed (gotcha
   * "floor-reload-program-lifetime"). `show` draws every unit, a down one lying
   * on its tile. A new floor (`floor_entered`) draws none: `unit_spawned` adds
   * the standing ones, and a down party unit has no tile there (game-core keeps
   * its old floor's position, and it can only be revived at its fall). While
   * the context is lost only the units and the state are taken: the floor is
   * built on restore. A rebuild after a lost context (`restoring`) keeps the
   * unit poses, the explored tiles (if they are this floor's) and the hidden
   * markers.
   */
  const replaceWorld = (state: RunState, withUnits: 'all' | 'none' | 'keep', leaderAt?: Pos, restoring = false): void => {
    if (theme === null || palette === null) throw new Error('show() the floor first');
    built = state;
    if (!restoring) {
      hiddenMarkers.clear();
      seenSnapshot = null;
    }
    if (withUnits !== 'keep') {
      units.clear();
      if (withUnits === 'all') for (const u of state.units) units.add(u);
      leaderId = state.party.find((id) => state.units.some((u) => u.id === id && !u.down)) ?? state.party[0] ?? null;
    }
    const lead = leaderPose();
    if (lead !== undefined && leaderAt !== undefined) {
      lead.x = leaderAt.x;
      lead.y = leaderAt.y;
    }
    if (lost) return;
    const started = performance.now();
    const next = buildWorld(state, seed, theme, palette, gpu.shared, tier, icons);
    const old = world;
    old?.group.removeFromParent();
    scene.add(next.group);
    world = next;
    scene.background = palette.background;
    gpu.setFog(palette.fog);
    if (restoring && seenSnapshot !== null && seenSnapshot.floor === state.floor) next.restoreSeen(seenSnapshot.tiles);
    seenSnapshot = null;
    for (const owner of hiddenMarkers) hideIn(next, owner, true);
    peak = { drawCalls: 0, triangles: 0 };
    revealLeader();
    // The unit meshes exist before the programs are compiled, so the first frame compiles nothing of its own;
    // once sheets are asked for, the sprite programs are compiled here too, before the first one lands.
    units.update(palette, visible, elevation, camera, performance.now());
    if (tier === 'medium') {
      gpu.medium.bindFloor(next, theme, seed);
      renderer.shadowMap.needsUpdate = true;
      precompile(renderer, scene, camera, gpu.medium.lens.target, gpu.medium.lens.materials, spriteStaging());
    } else {
      const staged = spriteStaging();
      if (staged !== null) precompileStaged(renderer, scene, camera, staged);
    }
    render();
    buildFrame = { drawCalls: last.drawCalls, triangles: last.triangles };
    old?.dispose();
    // One pixel read back waits for the GPU (shader compiles included), so the build time is to the first frame, not to its submission.
    const gl = renderer.getContext();
    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, syncPixel);
    buildMs = performance.now() - started;
  };

  /** Hides (or shows again) every part of a marker in a world. */
  const hideIn = (w: World, owner: string, hidden: boolean): void => {
    w.markers.boxes.setHidden(owner, hidden);
    w.markers.rounds.setHidden(owner, hidden);
    w.markers.glows.setHidden(owner, hidden);
    w.markers.water.setHidden(owner, hidden);
    w.billboards.setHidden(owner, hidden);
  };

  const revealLeader = (): void => {
    const lead = leaderPose();
    if (world !== null && lead) world.revealFrom({ x: Math.round(lead.x), y: Math.round(lead.y) });
  };

  const stage: Stage = {
    units,
    get palette(): Palette {
      if (palette === null) throw new Error('show() the floor first');
      return palette;
    },
    run: (ms, step) => clock.run(ms, step),
    enterFloor: (after) => {
      replaceWorld(after, 'none');
    },
    arrived: (unitId) => {
      if (unitId === leaderId) revealLeader();
    },
    visible,
    // Markers cast into the static shadow map at Medium: it is redrawn when one goes or moves.
    // Remembered for this floor, so a rebuild after a lost context hides them again (even one lost mid-command).
    setMarkerHidden: (owner, hidden) => {
      if (hidden) hiddenMarkers.add(owner);
      else hiddenMarkers.delete(owner);
      if (world !== null) hideIn(world, owner, hidden);
      if (tier === 'medium') renderer.shadowMap.needsUpdate = true;
    },
    setMarkerOffset: (owner, dy) => {
      world?.markers.boxes.setOffset(owner, dy);
      world?.markers.rounds.setOffset(owner, dy);
      world?.billboards.setOffset(owner, dy);
      if (tier === 'medium') renderer.shadowMap.needsUpdate = true;
    },
    ring: async (x, y, color, burst, ms) => {
      const ring = gpu.effects.ring(x, y, color, burst);
      await clock.run(ms, (t) => {
        ring.t = t;
      });
      gpu.effects.finish(ring);
    },
    setEgg: (at) => {
      world?.billboards.setEgg(at);
    },
    lastBoss: null,
  };

  // A lost context: stop drawing and free every GPU resource while GL calls are no-ops; rebuild on restore.
  const onLost = (e: globalThis.Event): void => {
    e.preventDefault();
    if (lost || disposed) return;
    lost = true;
    seenSnapshot = world === null ? null : { floor: world.floor, tiles: [...world.seen] };
    losses += 1;
    stopIdle();
    clock.breakStreak();
    world?.dispose();
    world = null;
    units.dispose();
    gpu.dispose();
  };
  const onRestored = (): void => {
    if (!lost || disposed) return;
    lost = false;
    restores += 1;
    gpu = createGpu(renderer, scene, gpu.gpu);
    units.rebind(gpu.shared.box, gpu.disc, gpu.shadowMaterial, gpu.shared.billboardLight);
    applyTier();
    // The floor the view was showing (or was given while lost), its hidden markers and explored tiles.
    if (built !== null && theme !== null) replaceWorld(built, 'keep', undefined, true);
    startIdle();
  };
  canvas.addEventListener('webglcontextlost', onLost);
  canvas.addEventListener('webglcontextrestored', onRestored);
  applyTier();

  const install = (sheet: SpriteSheet): void => {
    units.install(sheet);
    installedSprites.add(sheet.id);
  };
  /** Sheets that landed and wait for the next frame, and that frame's promise. */
  let landing: { readonly sheets: SpriteSheet[]; readonly done: Promise<void> } | null = null;
  /** A sheet landed: it installs with every other one that lands before the next frame, then one frame draws them. */
  const land = (sheet: SpriteSheet): Promise<void> => {
    if (landing === null) {
      const sheets: SpriteSheet[] = [];
      const done = new Promise<void>((resolve) => {
        const flush = (): void => {
          landing = null;
          if (!disposed) {
            for (const s of sheets) install(s);
            render();
            startIdle();
          }
          resolve();
        };
        // A hidden page draws no frames; it installs at once and draws when it is shown.
        if (doc.hidden) setTimeout(flush, 0);
        else requestAnimationFrame(flush);
      });
      landing = { sheets, done };
    }
    landing.sheets.push(sheet);
    return landing.done;
  };

  const ray = new THREE.Raycaster();
  const ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const pointer = new THREE.Vector2();
  const hit = new THREE.Vector3();

  const project = (x: number, y: number, z: number, seen: boolean): Projection => {
    const v = new THREE.Vector3(x, y, z).project(camera);
    const inside = v.z < 1 && Math.abs(v.x) <= 1 && Math.abs(v.y) <= 1;
    return { x: ((v.x + 1) / 2) * canvas.clientWidth, y: ((1 - v.y) / 2) * canvas.clientHeight, visible: seen && inside };
  };

  return {
    show(state, opts) {
      if (disposed) throw new Error('the view is disposed');
      theme = opts.theme;
      palette = paletteOf(opts.theme);
      mediumFog.set(opts.theme.lightRules.fogHue);
      icons = opts.itemIcons;
      seed = opts.seed;
      stage.lastBoss = null;
      // While the context is lost this takes the state and the units; the floor is built on restore.
      replaceWorld(state, 'all', opts.leaderAt);
      startIdle();
    },
    async play(events, _before, after, opts) {
      // A unit's facing after the command belongs to its last move, unless it turned after that move.
      const facingFromAfter = new Set<number>();
      const settled = new Set<string>();
      for (let i = events.length - 1; i >= 0; i -= 1) {
        const e = events[i];
        if (e === undefined || (e.type !== 'moved' && e.type !== 'turned')) continue;
        if (!settled.has(e.unit) && e.type === 'moved') facingFromAfter.add(i);
        settled.add(e.unit);
      }
      for (const [i, e] of events.entries()) {
        if (disposed) return;
        if (e.type === 'floor_entered' && opts?.seed !== undefined) seed = opts.seed;
        opts?.onEvent?.(e, i);
        const handled = await playEvent(stage, e, { after, facingFromAfter: facingFromAfter.has(i), next: events[i + 1] });
        // Counted after the case ran, from the type the case returned.
        counts[handled] = (counts[handled] ?? 0) + 1;
      }
      if (disposed) return;
      leaderId = after.party.find((id) => after.units.some((u) => u.id === id && !u.down)) ?? leaderId;
      // A sprite sleeps while the state says it is asleep (a sleep with no event of its own this command included).
      for (const unit of after.units) {
        const pose = units.get(unit.id);
        if (pose !== undefined) pose.sleeping = !pose.down && unit.statuses.some((st) => st.status === 'sleep');
      }
      revealLeader();
      // The first frame after play redraws the static shadow map once, whatever the sprites did.
      if (tier === 'medium') renderer.shadowMap.needsUpdate = true;
      render();
      startIdle();
    },
    setTier(nextTier) {
      if (disposed || nextTier === tier) return;
      tier = nextTier;
      clock.breakStreak();
      if (lost) return;
      applyTier();
      render();
      stopIdle();
      startIdle();
    },
    get tier() {
      return tier;
    },
    setSpeed: (speed) => {
      clock.setSpeed(speed);
      units.setSpeed(speed);
    },
    get busy() {
      return clock.busy;
    },
    resize(width, height) {
      size = { width, height };
      if (lost) return;
      applySize();
      render();
    },
    project(unitId) {
      const p = units.get(unitId);
      if (p === undefined) return null;
      const height = units.topOf(p) + presentation.labelLift;
      return project(p.x + p.lungeX + p.swayX, elevation(p) + p.lift + height, -(p.y + p.lungeY + p.swayY), visible(p) && !p.gone);
    },
    projectTile(pos) {
      return project(pos.x, 0, -pos.y, true);
    },
    pickTile(x, y) {
      if (world === null || canvas.clientWidth === 0 || canvas.clientHeight === 0) return null;
      pointer.set((x / canvas.clientWidth) * 2 - 1, 1 - (y / canvas.clientHeight) * 2);
      ray.setFromCamera(pointer, camera);
      if (ray.ray.intersectPlane(ground, hit) === null) return null;
      const tx = Math.round(hit.x);
      const ty = Math.round(-hit.z);
      const f = world.floor;
      if (tx < 0 || ty < 0 || tx >= f.width || ty >= f.height || !world.isSeen(tileIndex(f, tx, ty))) return null;
      return { x: tx, y: ty };
    },
    explored() {
      // While the context is lost the floor is gone; its explored tiles are kept for the rebuild.
      const floor = world?.floor ?? seenSnapshot?.floor ?? built?.floor ?? null;
      if (floor === null) return [];
      const seen: ReadonlySet<number> = world !== null ? world.seen : new Set(seenSnapshot?.tiles ?? []);
      return Array.from({ length: floor.width * floor.height }, (_, i) => seen.has(i));
    },
    stats: () => ({
      ...last,
      tier,
      pixelRatio: renderer.getPixelRatio(),
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      cpuFrameSamples: [...cpuSamples],
      animationFrameSamples: clock.animationIntervals(),
      gpuMs: gpu.gpu.last(),
      gpuSamples: gpu.gpu.samples(),
      gpuMeasured: gpu.gpu.measured(),
      buildMs,
      buildFrame,
      geometries: renderer.info.memory.geometries,
      textures: renderer.info.memory.textures,
      programs: renderer.info.programs?.length ?? 0,
      camera: { target: [...followed.target] as const, leader: followed.leader === null ? null : ([...followed.leader] as const), lead: leadFor(camera.aspect) },
      contextLosses: losses,
      contextRestores: restores,
      peak: { ...peak },
      lastFrame: { shadowRedraw },
      itemsShown: world?.billboards.itemsShown() ?? 0,
      sprites: { loaded: installedSprites.size, ...units.spriteCounts((p) => world !== null && visible(p)) },
      scene: null,
    }),
    resetPeak() {
      peak = { drawCalls: 0, triangles: 0 };
    },
    eventCounts: () => ({ ...counts }),
    async loadSprites(sources) {
      const waits: Promise<void>[] = [];
      let now = false;
      for (const source of sources) {
        if (requestedSprites.has(source.id)) continue;
        requestedSprites.add(source.id);
        const ready = preparedSheet(source);
        if (ready !== undefined) {
          install(ready);
          now = true;
          continue;
        }
        waits.push(
          loadSheet(source).then(land, (e: unknown) => {
            requestedSprites.delete(source.id);
            console.warn(`sprite ${source.id} not installed (it draws as its proxy): ${e instanceof Error ? e.message : String(e)}`);
          }),
        );
      }
      if (now) {
        render();
        startIdle();
      }
      await Promise.all(waits);
      return [...installedSprites];
    },
    spriteState: (unitId) => units.spriteState(unitId),
    dispose() {
      if (disposed) return { geometries: 0, textures: 0 };
      disposed = true;
      stopIdle();
      clock.dispose();
      doc.removeEventListener('visibilitychange', onVisibility);
      canvas.removeEventListener('webglcontextlost', onLost);
      canvas.removeEventListener('webglcontextrestored', onRestored);
      world?.dispose();
      units.dispose();
      gpu.dispose();
      const left = { geometries: renderer.info.memory.geometries, textures: renderer.info.memory.textures };
      // The next view's renderer (a scene's, on the same canvas) assumes three's default unpack state: put it back.
      const gl = renderer.getContext();
      if (!gl.isContextLost()) {
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      }
      renderer.dispose();
      return left;
    },
  };
};
